/* Native details navigation; event and scroll state belong only to this trial. */
(() => {
  if (customElements.get('th-preview-menu')) return;

  class ThPreviewNavigation extends HTMLElement {
    connectedCallback() {
      this.mobile = this.localName === 'th-preview-drawer';
      this.disclosure = this.querySelector('[data-th-preview-disclosure]');
      this.summary = this.disclosure.querySelector(':scope > summary');
      this.panel = this.querySelector('[data-th-preview-panel]');
      this.header = this.closest('.th-preview-header');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };

      this.addEventListener('toggle', (event) => this.onToggle(event), { ...options, capture: true });
      this.addEventListener('keydown', (event) => this.onKeyDown(event), options);
      document.addEventListener('pointerdown', (event) => {
        if (this.disclosure.open && !this.disclosure.contains(event.target)) this.close();
      }, { ...options, capture: true });
      document.addEventListener('focusin', (event) => {
        if (this.disclosure.open && !this.disclosure.contains(event.target)) this.close();
      }, options);
      this.addEventListener('click', (event) => {
        if (event.target.closest('a[href]') && this.disclosure.open) this.close();
      }, options);
      window.addEventListener('resize', () => this.onResize(), options);
      window.visualViewport?.addEventListener('resize', () => this.measurePanel(), options);
      window.addEventListener('scroll', () => this.measurePanel(), { ...options, passive: true });
      this.syncExpanded();
    }

    disconnectedCallback() {
      this.close();
      this.abort?.abort();
    }

    syncExpanded() {
      this.querySelectorAll('details').forEach((details) => {
        details.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(details.open));
      });
    }

    onToggle(event) {
      if (!(event.target instanceof HTMLDetailsElement)) return;
      this.syncExpanded();
      if (event.target !== this.disclosure) return;
      if (this.disclosure.open) {
        document.querySelectorAll('th-preview-menu, th-preview-drawer').forEach((other) => {
          if (other !== this) other.close?.();
        });
        this.measurePanel();
        if (this.mobile) this.lockScroll();
      } else {
        this.unlockScroll();
      }
    }

    measurePanel() {
      if (!this.disclosure.open) return;
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
      this.panel.style.setProperty('--th-preview-panel-height', `${Math.max(0, bottom - this.header.getBoundingClientRect().bottom)}px`);
    }

    lockScroll() {
      if (this.scrollLocked) return;
      this.scrollLocked = true;
      document.body.classList.add('th-preview-drawer-open');
      this.stickyHeader = this.closest('sticky-header');
      if (this.stickyHeader) {
        this.previousPreventHide = this.stickyHeader.preventHide;
        this.stickyHeader.preventHide = true;
      }
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      this.scrollLocked = false;
      document.body.classList.remove('th-preview-drawer-open');
      if (this.stickyHeader) this.stickyHeader.preventHide = this.previousPreventHide;
    }

    close(restoreFocus = false) {
      if (!this.disclosure) return;
      this.disclosure.open = false;
      this.disclosure.querySelectorAll('details[open]').forEach((details) => { details.open = false; });
      this.syncExpanded();
      this.unlockScroll();
      if (restoreFocus) this.summary.focus({ preventScroll: true });
    }

    onResize() {
      const desktop = window.matchMedia('(min-width: 990px)').matches;
      if ((this.mobile && desktop) || (!this.mobile && !desktop)) this.close();
      else this.measurePanel();
    }

    onKeyDown(event) {
      if (event.key === 'Escape' && this.disclosure.open) {
        // Let an open country/language selector consume its own Escape first.
        if (event.target.closest('localization-form')) return;
        const group = event.target.closest('.th-preview-mobile-group[open]');
        event.preventDefault();
        event.stopPropagation();
        if (group) {
          group.open = false;
          this.syncExpanded();
          group.querySelector('summary').focus({ preventScroll: true });
        } else this.close(true);
        return;
      }
      if (event.key === 'ArrowDown' && event.target === this.summary) {
        event.preventDefault();
        this.disclosure.open = true;
        this.syncExpanded();
        this.measurePanel();
        this.panel.querySelector('a[href]')?.focus({ preventScroll: true });
        return;
      }
      if (event.key !== 'Tab' || !this.mobile || !this.disclosure.open) return;
      const focusable = Array.from(this.disclosure.querySelectorAll(
        'summary, a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((node) => node.getClientRects().length && !node.closest('[inert]'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && event.target === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && event.target === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('th-preview-menu', class extends ThPreviewNavigation {});
  customElements.define('th-preview-drawer', class extends ThPreviewNavigation {});
})();
